import { useCallback, useEffect, useRef, useState } from 'react'
import Sheet from './Sheet.jsx'
import Links from './Links.jsx'
import {
  addTopic, addTopicEntry, deleteEntry, deleteTopic, getTopicEntries, renameTopic, updateEntry,
} from '../lib/notes.js'

/*
 * A note, open. It reads like a journal on one subject: oldest at the top,
 * each addition stamped with when it was written, and the box to add the
 * next one waiting at the bottom. Years of thinking on one thing, in order.
 */

const monthLabel = (iso) => new Date(iso).toLocaleDateString('en-PH', { month: 'long', year: 'numeric' })
const stamp = (iso) => new Date(iso).toLocaleString('en-PH', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})
const fullStamp = (iso) => new Date(iso).toLocaleString('en-PH', {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
})
const shortDay = (iso) => new Date(iso).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })

/* The sheet on a phone, the detail pane on a Mac: whichever ancestor is
   actually doing the scrolling. Scrolling that one directly, rather than
   scrollIntoView, keeps the page behind the sheet from moving too. */
function scrollParent(el) {
  for (let n = el?.parentElement; n; n = n.parentElement) {
    const o = getComputedStyle(n).overflowY
    if ((o === 'auto' || o === 'scroll') && n.scrollHeight > n.clientHeight) return n
  }
  return null
}

async function copyText(text, toast, done = 'Copied') {
  try {
    await navigator.clipboard.writeText(text)
    toast?.(done)
  } catch {
    toast?.('Could not copy that.')
  }
}

/* A new note has no title to ask for. Like Apple Notes, its first line names
   it, trimmed to something that fits a row; Rename is there afterwards. */
function titleFrom(text) {
  const line = text.trim().split('\n')[0].trim()
  return line.length > 60 ? line.slice(0, 57).trimEnd() + '…' : line
}

export default function NoteSheet({ note, categoryId, toast, onClose, onChanged, onCreated }) {
  const [title, setTitle] = useState(note?.title ?? '')
  const [entries, setEntries] = useState(note ? null : [])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [acting, setActing] = useState(null)
  const [editing, setEditing] = useState(null)
  const [renaming, setRenaming] = useState(false)
  const [removing, setRemoving] = useState(false)
  const endRef = useRef(null)
  const pinToEnd = useRef(true)

  const load = useCallback(async () => {
    if (!note) return
    try {
      setEntries(await getTopicEntries(note.id))
    } catch (e) {
      toast?.(e.message || 'Could not load this note.')
      setEntries([])
    }
  }, [note, toast])

  useEffect(() => {
    pinToEnd.current = true
    if (note) setTitle(note.title)
    load()
  }, [load, note])

  /* Open at the newest entry, where he left off, and follow each new one. */
  useEffect(() => {
    if (!entries || !pinToEnd.current) return
    pinToEnd.current = false
    window.requestAnimationFrame(() => {
      const scroller = scrollParent(endRef.current)
      if (scroller) scroller.scrollTop = scroller.scrollHeight
    })
  }, [entries])

  async function add(e) {
    e?.preventDefault()
    if (busy || !draft.trim()) return
    setBusy(true)
    try {
      if (!note) {
        /* The note is only created once there is something in it, so an
           opened-and-abandoned New note leaves nothing behind. */
        const topic = await addTopic({ title: titleFrom(draft), categoryId })
        await addTopicEntry(topic, draft)
        setDraft('')
        await onCreated?.(topic)
        return
      }
      await addTopicEntry({ ...note, title }, draft)
      setDraft('')
      pinToEnd.current = true
      await load()
      onChanged?.()
    } catch (err) {
      toast?.(err.message || 'Could not add that.')
    } finally {
      setBusy(false)
    }
  }

  function copyAll() {
    const body = (entries ?? []).map((x) => fullStamp(x.created_at) + '\n' + x.body).join('\n\n')
    copyText(title + '\n\n' + body, toast, 'Note copied')
  }

  const list = entries ?? []
  const groups = []
  for (const x of list) {
    const label = monthLabel(x.created_at)
    if (groups.at(-1)?.label !== label) groups.push({ label, items: [] })
    groups.at(-1).items.push(x)
  }

  return (
    <Sheet title={note ? title : 'New note'} onClose={onClose}>
      {note ? (<>
      <div className="note-meta">
        <span className="lbl">
          Started {shortDay(note.created_at)}
          {entries ? ' · ' + list.length + (list.length === 1 ? ' entry' : ' entries') : ''}
        </span>
        <span className="note-meta-actions">
          <button type="button" className="linkbtn" onClick={() => setRenaming(true)}>Rename</button>
          {list.length ? (
            <button type="button" className="linkbtn" onClick={copyAll}>Copy all</button>
          ) : null}
        </span>
      </div>

      <Links target={{ type: 'topic', id: note.id }} toast={toast} compact />
      </>) : null}

      {!entries ? <p className="lbl">Loading…</p> : null}
      {note && entries && !list.length ? (
        <p className="note-empty">Nothing here yet. Write the first line below.</p>
      ) : null}

      <div className="note-thread">
        {groups.map((g) => (
          <section key={g.label} aria-label={g.label}>
            <p className="note-month">{g.label}</p>
            {g.items.map((x) => (
              <button
                type="button"
                className="note-entry"
                key={x.id}
                aria-label={'Entry from ' + fullStamp(x.created_at) + ': ' + x.body}
                onClick={() => setActing(x)}
              >
                <span className="note-body">{x.body}</span>
                <span className="note-when">{stamp(x.created_at)}</span>
              </button>
            ))}
          </section>
        ))}
      </div>

      <form className="note-composer" onSubmit={add} ref={endRef}>
        <label className="sr-only" htmlFor={'note-add-' + (note?.id ?? 'new')}>
          {note ? 'Add to this note' : 'Start a new note'}
        </label>
        <textarea
          id={'note-add-' + (note?.id ?? 'new')}
          rows={note ? 2 : 4}
          autoFocus={!note}
          placeholder={note ? 'Add to this note…' : 'Start writing. The first line becomes the title.'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) add(e)
          }}
        />
        <button className="btn" type="submit" disabled={busy || !draft.trim()}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </form>

      {note ? (
        <button type="button" className="linkbtn note-remove" onClick={() => setRemoving(true)}>
          Delete note
        </button>
      ) : null}

      {removing ? (
        <DeleteNote
          note={{ ...note, title }}
          count={list.length}
          toast={toast}
          onClose={() => setRemoving(false)}
          onDeleted={() => { setRemoving(false); onChanged?.(); onClose() }}
        />
      ) : null}

      {acting ? (
        <EntryActions
          entry={acting}
          toast={toast}
          onClose={() => setActing(null)}
          onEdit={() => { setEditing(acting); setActing(null) }}
          onDeleted={async () => { setActing(null); await load(); onChanged?.() }}
        />
      ) : null}

      {editing ? (
        <TextEdit
          title="Edit entry"
          label={'Written ' + fullStamp(editing.created_at)}
          initial={editing.body}
          multiline
          toast={toast}
          onClose={() => setEditing(null)}
          onSave={async (v) => {
            if (v !== editing.body) await updateEntry(editing.id, v)
            setEditing(null)
            await load()
          }}
        />
      ) : null}

      {renaming ? (
        <TextEdit
          title="Rename note"
          label="Title"
          initial={title}
          toast={toast}
          onClose={() => setRenaming(false)}
          onSave={async (v) => {
            if (v !== title) await renameTopic(note.id, v)
            setTitle(v)
            setRenaming(false)
            onChanged?.()
          }}
        />
      ) : null}
    </Sheet>
  )
}

function DeleteNote({ note, count, toast, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false)
  return (
    <Sheet title="Delete this note?" onClose={onClose}>
      <p className="note-quote">{note.title}</p>
      <p className="lbl" style={{ margin: '0 2px 10px' }}>
        {count
          ? 'Its ' + count + (count === 1 ? ' entry goes' : ' entries go') + ' with it, and any links to it are removed. '
          : 'Any links to it are removed. '}
        The Log keeps a record that it existed.
      </p>
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Keep</button>
        <button
          type="button"
          className="btn danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true)
            try {
              await deleteTopic(note)
              onDeleted()
            } catch (e) {
              toast?.(e.message || 'Could not delete that note.')
              setBusy(false)
            }
          }}
        >
          {busy ? 'Deleting…' : 'Delete note'}
        </button>
      </div>
    </Sheet>
  )
}

function EntryActions({ entry, toast, onClose, onEdit, onDeleted }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title={fullStamp(entry.created_at)} onClose={onClose}>
      <p className="note-quote">{entry.body}</p>
      {confirming ? (
        <>
          <p className="lbl" style={{ margin: '4px 2px 10px' }}>
            Delete this entry? The Log keeps a record of what it said.
          </p>
          <div className="sheet-actions">
            <button type="button" className="btn ghost" onClick={() => setConfirming(false)}>Keep</button>
            <button
              type="button"
              className="btn danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true)
                try {
                  await deleteEntry(entry)
                  await onDeleted()
                } catch (e) {
                  toast?.(e.message || 'Could not delete that.')
                  setBusy(false)
                }
              }}
            >
              {busy ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </>
      ) : (
        <div className="note-actions">
          <button type="button" className="btn ghost" onClick={() => { copyText(entry.body, toast); onClose() }}>Copy</button>
          <button type="button" className="btn ghost" onClick={onEdit}>Edit</button>
          <button type="button" className="btn ghost note-delete" onClick={() => setConfirming(true)}>Delete</button>
        </div>
      )}
    </Sheet>
  )
}

function TextEdit({ title, label, initial, multiline = false, onClose, onSave, toast }) {
  const [v, setV] = useState(initial)
  const [busy, setBusy] = useState(false)
  return (
    <Sheet title={title} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !v.trim()) return
          setBusy(true)
          try {
            await onSave(v.trim())
          } catch (err) {
            toast?.(err.message || 'Could not save that.')
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="note-edit-v">{label}</label>
          {multiline ? (
            <textarea id="note-edit-v" rows={5} value={v} onChange={(e) => setV(e.target.value)} required />
          ) : (
            <input id="note-edit-v" value={v} onChange={(e) => setV(e.target.value)} required />
          )}
        </div>
        <button className="btn" type="submit" disabled={busy || !v.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Sheet>
  )
}
